import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Calendar, Clock, User } from 'lucide-react';
import brain from 'brain';
import { toast } from 'sonner';

interface AppointmentConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appointmentId: string;
  appointmentDetails: {
    clientName?: string;
    serviceName?: string;
    startTime?: string;
    endTime?: string;
    staffNames?: string[];
  };
  onConfirmed?: () => void;
  notificationId?: string; // Add optional notificationId
}

export function AppointmentConfirmationDialog({
  open,
  onOpenChange,
  appointmentId,
  appointmentDetails,
  onConfirmed,
  notificationId, // Destructure notificationId
}: AppointmentConfirmationDialogProps) {
  const [isLoading, setIsLoading] = useState(false);

  const formatDateTime = (dateTimeString?: string) => {
    if (!dateTimeString) return 'N/A';
    try {
      const date = new Date(dateTimeString);
      return date.toLocaleString('en-AU', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateTimeString;
    }
  };

  const formatTime = (dateTimeString?: string) => {
    if (!dateTimeString) return 'N/A';
    try {
      const date = new Date(dateTimeString);
      return date.toLocaleString('en-AU', {
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateTimeString;
    }
  };

  const handleConfirmation = async (confirmed: boolean) => {
    setIsLoading(true);
    try {
      const response = await brain.confirm_appointment(
        { appointmentId },
        { confirmed }
      );

      if (response.ok) {
        const data = await response.json();
        if (confirmed) {
          toast.success('Appointment Confirmed', {
            description: 'Thank you for confirming your appointment.',
          });
        } else {
          toast.success('Appointment Cancelled', {
            description: 'Your appointment has been cancelled. The admin team has been notified.',
          });
        }
        
        // Mark notification as read after user takes action
        if (notificationId) {
          try {
            const { useNotificationStore } = await import('utils/notificationStore');
            await useNotificationStore.getState().markAsRead(notificationId);
          } catch (error) {
            console.error('Error marking notification as read:', error);
          }
        }
        
        onOpenChange(false);
        onConfirmed?.();
      } else {
        const error = await response.json();
        toast.error('Error', {
          description: error.detail || 'Failed to process your response. Please try again.',
        });
      }
    } catch (error) {
      console.error('Error confirming appointment:', error);
      toast.error('Error', {
        description: 'Failed to process your response. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-2xl">Confirm Your Appointment</DialogTitle>
          <DialogDescription className="text-base pt-2">
            Your appointment is scheduled for 48 hours from now. Please confirm if you'll be attending.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="bg-muted/50 rounded-lg p-4 space-y-3">
            {appointmentDetails.serviceName && (
              <div className="flex items-start gap-3">
                <User className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Service</p>
                  <p className="text-base font-semibold">{appointmentDetails.serviceName}</p>
                </div>
              </div>
            )}

            {appointmentDetails.startTime && (
              <div className="flex items-start gap-3">
                <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Date & Time</p>
                  <p className="text-base font-semibold">
                    {formatDateTime(appointmentDetails.startTime)}
                  </p>
                  {appointmentDetails.endTime && (
                    <p className="text-sm text-muted-foreground">
                      Until {formatTime(appointmentDetails.endTime)}
                    </p>
                  )}
                </div>
              </div>
            )}

            {appointmentDetails.staffNames && appointmentDetails.staffNames.length > 0 && (
              <div className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Support Worker(s)</p>
                  <p className="text-base font-semibold">
                    {appointmentDetails.staffNames.join(', ')}
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <p className="text-sm text-blue-900 dark:text-blue-100">
              <strong>Please confirm:</strong> Will you be attending this appointment?
            </p>
          </div>
        </div>

        <DialogFooter className="flex-row gap-2">
          <Button
            variant="outline"
            onClick={() => handleConfirmation(false)}
            disabled={isLoading}
            className="flex-1 border-red-200 hover:bg-red-50 hover:text-red-700 dark:border-red-800 dark:hover:bg-red-950 aspect-square sm:aspect-auto"
          >
            No
          </Button>
          <Button
            onClick={() => handleConfirmation(true)}
            disabled={isLoading}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white aspect-square sm:aspect-auto"
          >
            Yes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
